import { moveCard } from "@lourenci/react-kanban";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { IIssueBoardApi } from "@mcapps/shared";
import { IUpdateIssueResp } from "@mctypes/tempTypes";
import { IIssueApi } from "@schemas/issueSchema";
import {
  GetProjectByProjectIdConnectedTasksApiArg,
  GetProjectByProjectIdIssuesOnBoardApiArg,
  GetProjectByProjectIdIssuesOnBoardOldCompletedApiArg,
  GetProjectByProjectIdMeetingAndMeetingIdApiArg,
  openapi,
  PostProjectByProjectIdIssuesOnBoardsApiArg,
  PostProjectByProjectIdIssuesOnBoardsApiResponse,
  PutProjectByProjectIdIssuesKanbanreorderApiResponse,
  PutProjectByProjectIdIssueUpdateSimpleApiArg,
} from "../../../../openapi";
import { apiSlice } from "../../../apiSlice";
import {
  setNotificationInRedux,
  setShowFailedMessageThunk,
  setShowSuccessMessageThunk,
} from "../../../global/globalSlice";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import { classifyTidsfrist, computeDueForTidsfristBucket } from "@mcapps/shared/sharedUtils";
import { IRootState } from "../../../store";
import { trpcSlice } from "../../../trpcSlice";
import { multiRegisterIssuesApi } from "./MultiRegisterIssues/multiRegisterIssuesApi";
import { selectHttpIssuesOnBoard, selectKanbanBoardColumns } from "./issueSelectors";
import { addSelectedIssueIdsInRedux, removeSelectedIssueIdsInRedux, setIssueSortedColInRedux } from "./issueSlice";
import { addMissingKeysIssue } from "./issueUtils";
import { IKanbanBoard, IKanbanCard, IKanbanFrom, IKanbanTo } from "./types";

const SET_ACTIVE_TEMPLATE_ACTION = "issue/setIssueActiveTableTemplateIdInRedux";
type IssuePropertyPatch = PutProjectByProjectIdIssueUpdateSimpleApiArg["body"]["property"];
type QueryCacheState = { queries?: Record<string, object> };
type StateWithQueryCaches = { [reducerPath: string]: QueryCacheState | undefined };
type AppGetState = () => object;
type AppDispatch = ThunkDispatch<IRootState, object, Action>;
type IdLike = string | number | null | undefined | { toString(): string };
type PatchableIssueValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | { [key: string]: PatchableIssueValue }
  | PatchableIssueValue[];
type PatchableIssueRecord = { [key: string]: PatchableIssueValue | undefined };
type KanbanOrderAttribute = "status" | "bucket" | "tidsfrist";
const sameId = (a: IdLike, b: IdLike) => String(a) === String(b);
const getIdString = (value: IdLike | { _id?: IdLike } | null | undefined): string | undefined => {
  if (value == null) {
    return undefined;
  }

  if (typeof value === "object" && "_id" in value) {
    return value._id == null ? undefined : String(value._id);
  }

  return String(value);
};

const getStateFromMaybeCallback = (stateOrGetState: object | AppGetState): object =>
  typeof stateOrGetState === "function" ? stateOrGetState() : stateOrGetState;

const getQueriesForApi = (stateOrGetState: object | AppGetState, api: { reducerPath: string }) => {
  const state = getStateFromMaybeCallback(stateOrGetState) as StateWithQueryCaches;
  return state[api.reducerPath]?.queries ?? {};
};

const getKanbanOrderValue = (issue: IIssueApi, attr: KanbanOrderAttribute) => issue.kanbanOrder?.[attr] ?? 0;

const issueBoardHasAutomaticFormula = (issueBoard?: IIssueBoardApi | null) =>
  Object.values(issueBoard?.otherAttributesConfig ?? {}).some(
    (attribute) =>
      attribute.typeOfValue === "FORMULA" && attribute.isActive === true && attribute.formula?.counterMode !== "MANUAL",
  );

const shouldInvalidateIssueFormulasAfterPropertyPatch = (
  projectId: string,
  issue: IIssueApi,
  property: IssuePropertyPatch,
  state: IRootState,
) => {
  if (property.otherAttributesData == null) {
    return false;
  }

  const issueBoardId = issue.issueBoard?._id;
  if (issueBoardId == null) {
    return false;
  }

  const issueBoard = openapi.endpoints.getProjectByProjectIdIssueboardAndIssueBoardId.select({
    projectId,
    issueBoardId,
  })(state).data;

  return issueBoardHasAutomaticFormula(issueBoard);
};

function getDoGetAllIssuesArgs(
  stateOrGetState: IRootState | AppGetState,
  api: { reducerPath: string },
): GetProjectByProjectIdIssuesOnBoardApiArg[] {
  const queries = getQueriesForApi(stateOrGetState, api);
  const out: GetProjectByProjectIdIssuesOnBoardApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getProjectByProjectIdIssuesOnBoard(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      const args = JSON.parse(json) as GetProjectByProjectIdIssuesOnBoardApiArg;
      out.push(args);
    } catch {
      // ignore parse error
    }
  }

  return out;
}

function getOldCompletedIssuesOnBoardArgs(
  stateOrGetState: IRootState | AppGetState,
  api: { reducerPath: string },
): GetProjectByProjectIdIssuesOnBoardOldCompletedApiArg[] {
  const queries = getQueriesForApi(stateOrGetState, api);
  const out: GetProjectByProjectIdIssuesOnBoardOldCompletedApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getProjectByProjectIdIssuesOnBoardOldCompleted(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      const args = JSON.parse(json) as GetProjectByProjectIdIssuesOnBoardOldCompletedApiArg;
      out.push(args);
    } catch {
      // ignore parse error
    }
  }

  return out;
}

function getDoGetAllIssuesArgsForBoard(
  stateOrGetState: IRootState | AppGetState,
  api: { reducerPath: string },
  _projectId: string,
  _issueBoardId: string,
): GetProjectByProjectIdIssuesOnBoardApiArg[] {
  return getDoGetAllIssuesArgs(stateOrGetState, api);
}

function getMultiRegisterIssuesArgs(
  stateOrGetState: IRootState | AppGetState,
  api: { reducerPath: string },
): PostProjectByProjectIdIssuesOnBoardsApiArg[] {
  const queries = getQueriesForApi(stateOrGetState, api);
  const out: PostProjectByProjectIdIssuesOnBoardsApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getMultiRegisterIssuesOnBoards(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      out.push(JSON.parse(json) as PostProjectByProjectIdIssuesOnBoardsApiArg);
    } catch {
      // ignore parse error
    }
  }

  return out;
}

function getConnectedTasksArgs(
  stateOrGetState: IRootState | AppGetState,
  api: { reducerPath: string },
): GetProjectByProjectIdConnectedTasksApiArg[] {
  const queries = getQueriesForApi(stateOrGetState, api);
  const out: GetProjectByProjectIdConnectedTasksApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getProjectByProjectIdConnectedTasks(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      out.push(JSON.parse(json) as GetProjectByProjectIdConnectedTasksApiArg);
    } catch {
      // ignore parse error
    }
  }

  return out;
}

function getMeetingArgs(
  stateOrGetState: IRootState | AppGetState,
  api: { reducerPath: string },
): GetProjectByProjectIdMeetingAndMeetingIdApiArg[] {
  const queries = getQueriesForApi(stateOrGetState, api);
  const out: GetProjectByProjectIdMeetingAndMeetingIdApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getProjectByProjectIdMeetingAndMeetingId(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      out.push(JSON.parse(key.slice(open + 1, -1)) as GetProjectByProjectIdMeetingAndMeetingIdApiArg);
    } catch {
      // ignore parse error
    }
  }

  return out;
}

export const addConnectedTaskToParentIssueCaches = ({
  dispatch,
  getState,
  issueBoardId,
  parentIssueId,
  projectId,
  task,
}: {
  dispatch: AppDispatch;
  getState: AppGetState;
  issueBoardId: string;
  parentIssueId?: IdLike | { _id?: IdLike } | null;
  projectId: string;
  task: IIssueApi;
}) => {
  const parentId = getIdString(parentIssueId);
  if (parentId == null) {
    return;
  }

  const addTaskToIssue = (parentIssue?: IIssueApi) => {
    if (parentIssue == null) {
      return;
    }

    parentIssue.connectedTasks ??= [];
    if (!parentIssue.connectedTasks.some((connectedTask) => sameId(connectedTask._id, task._id))) {
      parentIssue.connectedTasks.push(task as (typeof parentIssue.connectedTasks)[number]);
    }
  };

  const addTask = (draft: IIssueApi[]) => {
    addTaskToIssue(draft.find((issue) => sameId(issue._id, parentId)));
  };

  dispatch(
    openapi.util.updateQueryData(
      "getProjectByProjectIdIssuesGetSingle",
      { projectId, issueId: parentId },
      (draft: IIssueApi) => {
        addTaskToIssue(draft);
      },
    ),
  );

  for (const args of getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId)) {
    dispatch(openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, addTask));
  }

  for (const args of getOldCompletedIssuesOnBoardArgs(getState, trpcSlice)) {
    dispatch(openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoardOldCompleted", args, addTask));
  }

  for (const args of getConnectedTasksArgs(getState, trpcSlice)) {
    if (!sameId(args.projectId, projectId) || !sameId(args.issueId, parentId)) {
      continue;
    }

    dispatch(
      openapi.util.updateQueryData("getProjectByProjectIdConnectedTasks", args, (draft: IIssueApi[]) => {
        if (!draft.some((connectedTask) => sameId(connectedTask._id, task._id))) {
          draft.push(task);
        }
      }),
    );
  }
};

const removeConnectedTaskFromParentIssueCaches = ({
  dispatch,
  getState,
  issueBoardId,
  parentIssueId,
  projectId,
  taskId,
}: {
  dispatch: AppDispatch;
  getState: AppGetState;
  issueBoardId: string;
  parentIssueId?: IdLike | { _id?: IdLike } | null;
  projectId: string;
  taskId: IdLike;
}) => {
  const parentId = getIdString(parentIssueId);

  const removeTaskFromIssue = (issue?: IIssueApi) => {
    const connectedTasks = issue?.connectedTasks as IIssueApi[] | undefined;
    if (!Array.isArray(connectedTasks)) {
      return;
    }

    const taskIndex = connectedTasks.findIndex((task) => sameId(task._id, taskId));
    if (taskIndex >= 0) {
      connectedTasks.splice(taskIndex, 1);
    }
  };

  const removeTask = (draft: IIssueApi[]) => {
    for (const issue of draft) {
      if (parentId != null && !sameId(issue._id, parentId)) {
        continue;
      }

      removeTaskFromIssue(issue);
    }
  };

  if (parentId != null) {
    dispatch(
      openapi.util.updateQueryData(
        "getProjectByProjectIdIssuesGetSingle",
        { projectId, issueId: parentId },
        (draft: IIssueApi) => {
          removeTaskFromIssue(draft);
        },
      ),
    );
  }

  for (const args of getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId)) {
    dispatch(openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, removeTask));
  }

  for (const args of getOldCompletedIssuesOnBoardArgs(getState, trpcSlice)) {
    dispatch(openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoardOldCompleted", args, removeTask));
  }

  for (const args of getConnectedTasksArgs(getState, trpcSlice)) {
    if (!sameId(args.projectId, projectId) || (parentId != null && !sameId(args.issueId, parentId))) {
      continue;
    }

    dispatch(
      openapi.util.updateQueryData("getProjectByProjectIdConnectedTasks", args, (draft: IIssueApi[]) => {
        const taskIndex = draft.findIndex((task) => sameId(task._id, taskId));
        if (taskIndex >= 0) {
          draft.splice(taskIndex, 1);
        }
      }),
    );
  }
};

const issueMatchesMultiRegisterQuery = (issue: IIssueApi, args: PostProjectByProjectIdIssuesOnBoardsApiArg) => {
  const issueBoardId = String(issue.issueBoard?._id ?? "");
  const filter = args.body.filter;

  if (!args.body.issueboardIds?.some((boardId) => String(boardId) === issueBoardId)) return false;
  if (filter?.issueboardIds != null && !filter.issueboardIds.some((boardId) => String(boardId) === issueBoardId)) {
    return false;
  }
  if (filter?.taskOrIssue != null && issue.taskOrIssue !== filter.taskOrIssue) return false;
  if (filter?.status != null && filter.status.length > 0 && !filter.status.includes(issue.status)) return false;
  if (
    filter?.assignedToIds != null &&
    filter.assignedToIds.length > 0 &&
    !filter.assignedToIds.includes(String(issue.assignedTo?._id ?? ""))
  ) {
    return false;
  }

  return true;
};

const decrementBoardCount = (
  boardCounts: PostProjectByProjectIdIssuesOnBoardsApiResponse["boardCounts"],
  issueBoardId: string,
) => {
  const count = boardCounts.find((item) => String(item.issueboardId) === issueBoardId);
  if (count != null) count.count = Math.max(count.count - 1, 0);
};

const incrementBoardCount = (
  boardCounts: PostProjectByProjectIdIssuesOnBoardsApiResponse["boardCounts"],
  issueBoardId: string,
) => {
  const count = boardCounts.find((item) => String(item.issueboardId) === issueBoardId);
  if (count != null) {
    count.count += 1;
  } else {
    boardCounts.push({ issueboardId: issueBoardId, count: 1 });
  }
};

const isPlainRecord = (value: PatchableIssueValue | undefined): value is PatchableIssueRecord =>
  value != null && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype;

const mergeIssuePropertyPatch = (target: PatchableIssueRecord, patch: PatchableIssueRecord) => {
  for (const [key, value] of Object.entries(patch)) {
    if (isPlainRecord(value) && isPlainRecord(target[key])) {
      mergeIssuePropertyPatch(target[key], value);
    } else {
      target[key] = value;
    }
  }
};

const patchIssueOptimistically = (
  projectId: string,
  issueId: string,
  property: IssuePropertyPatch,
  getState: AppGetState,
  dispatch: AppDispatch,
) => {
  const patches: { undo?: () => void }[] = [];
  const patchIssue = (issue: IIssueApi) =>
    mergeIssuePropertyPatch(issue as PatchableIssueRecord, property as PatchableIssueRecord);

  patches.push(
    dispatch(
      openapi.util.updateQueryData(
        "getProjectByProjectIdIssuesGetSingle",
        { projectId, issueId },
        (draft: IIssueApi) => {
          if (draft && sameId(draft._id, issueId)) {
            patchIssue(draft);
          }
        },
      ),
    ),
  );

  for (const args of getDoGetAllIssuesArgs(getState, trpcSlice)) {
    patches.push(
      dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
          const issue = draft.find((item) => sameId(item._id, issueId));
          if (issue != null) {
            patchIssue(issue);
          }
        }),
      ),
    );
  }

  for (const args of getOldCompletedIssuesOnBoardArgs(getState, trpcSlice)) {
    patches.push(
      dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoardOldCompleted", args, (draft: IIssueApi[]) => {
          const issue = draft.find((item) => sameId(item._id, issueId));
          if (issue != null) {
            patchIssue(issue);
          }
        }),
      ),
    );
  }

  for (const args of getMultiRegisterIssuesArgs(getState, trpcSlice)) {
    patches.push(
      dispatch(
        multiRegisterIssuesApi.util.updateQueryData("getMultiRegisterIssuesOnBoards", args, (draft) => {
          const idx = draft.items.findIndex((item) => sameId(item._id, issueId));
          if (idx === -1) return;

          const oldIssue = draft.items[idx] as IIssueApi;
          patchIssue(oldIssue);

          if (issueMatchesMultiRegisterQuery(oldIssue, args) === false) {
            draft.items.splice(idx, 1);
            decrementBoardCount(draft.boardCounts, String(oldIssue.issueBoard?._id ?? ""));
            if (draft.totalCount != null) draft.totalCount = Math.max(draft.totalCount - 1, 0);
          }
        }),
      ),
    );
  }

  return patches;
};

export const patchMultiRegisterIssueCaches = (issue: IIssueApi, getState: AppGetState, dispatch: AppDispatch) => {
  if (!issue?._id) return;

  const argsList = getMultiRegisterIssuesArgs(getState, trpcSlice);

  for (const args of argsList) {
    dispatch(
      multiRegisterIssuesApi.util.updateQueryData("getMultiRegisterIssuesOnBoards", args, (draft) => {
        const idx = draft.items.findIndex((item) => sameId(item._id, issue._id));
        const shouldKeepInResult = issueMatchesMultiRegisterQuery(issue, args);

        if (idx === -1) return;

        const oldIssue = draft.items[idx];
        const oldBoardId = String(oldIssue.issueBoard?._id ?? "");
        const newBoardId = String(issue.issueBoard?._id ?? "");

        if (shouldKeepInResult) {
          Object.assign(draft.items[idx], issue);
          if (oldBoardId !== newBoardId) {
            decrementBoardCount(draft.boardCounts, oldBoardId);
            incrementBoardCount(draft.boardCounts, newBoardId);
          }
        } else {
          draft.items.splice(idx, 1);
          decrementBoardCount(draft.boardCounts, oldBoardId);
          if (draft.totalCount != null) draft.totalCount = Math.max(draft.totalCount - 1, 0);
        }
      }),
    );
  }
};

openapi.enhanceEndpoints({
  addTagTypes: ["FormulaGroupIssues", "TrpcIssueBoard", "TrpcSingleIssueBoard", "TrpcIssueFormulas"],
  endpoints: {
    getProjectByProjectIdIssueboard: {
      providesTags: ["TrpcIssueBoard"],
    },
    getProjectByProjectIdIssueboardAndIssueBoardId: {
      providesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboard: {
      invalidatesTags: ["TrpcIssueBoard"],
    },
    getProjectByProjectIdIssueboardTemplateAll: {
      providesTags: ["TrpcIssueBoard"],
    },
    postProjectByProjectIdIssueboardImportExcel: {
      invalidatesTags: ["TrpcIssueBoard"],
    },
    putProjectByProjectIdIssueboardAndBoardId: {
      invalidatesTags: ["TrpcSingleIssueBoard", "Issues"],
    },
    putProjectByProjectIdIssueboardAndBoardIdBuckets: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    putProjectByProjectIdIssueboardAndBoardIdUsebucketsasworkflow: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    putProjectByProjectIdIssueboardAndBoardIdTypes: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    putProjectByProjectIdIssueboardAndBoardIdAccess: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    putProjectByProjectIdIssueboardAndBoardIdDisabledColumns: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardOtherAttributesConfigAdd: {
      invalidatesTags: ["TrpcSingleIssueBoard", "TrpcIssueFormulas"],
    },
    postProjectByProjectIdIssueboardOtherAttributesConfigUpdate: {
      invalidatesTags: ["TrpcSingleIssueBoard", "TrpcIssueFormulas"],
    },
    postProjectByProjectIdIssueboardOtherAttributesConfigUpdateorder: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardUseonlypredefinedtagsUpdate: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardPredefinedtagsUpdate: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardUsepredefinedtagsUpdate: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardRisikoIsactive: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardRisikoProbabilityIsactiveaftertiltak: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardRisikoProbabilityUpdate: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardRisikoEvaluationcategoryAdd: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardRisikoEvaluationcategoryUpdate: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardMoveIssueboardToProject: {
      invalidatesTags: ["TrpcIssueBoard"],
    },
    postProjectByProjectIdIssueboardNoteboardImageNodeAdd: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardNoteboardImageNodeUpdate: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    postProjectByProjectIdIssueboardNoteboardImageNodeDelete: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    putProjectByProjectIdGanttVacations: {
      invalidatesTags: ["TrpcSingleIssueBoard"],
    },
    getProjectByProjectIdIssuesTrpcIssueGetConnectedIssues: {
      providesTags: ["ConnectedIssues"],
    },
    getProjectByProjectIdIssuesGetSingle: {
      providesTags: ["ConnectedIssues", "Issue"],
    },
    getProjectByProjectIdIssueFormulaGroupIssues: {
      providesTags: ["FormulaGroupIssues"],
    },
    postProjectByProjectIdIssuesFileDescription: {
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(apiSlice.util.invalidateTags(["AzureBlobStorage"]));
      },
    },
    putProjectByProjectIdIssuesKanbanreorder: {
      invalidatesTags: ["Issue", "ConnectedIssues"],
      async onQueryStarted({ projectId, body }, { dispatch, getState, queryFulfilled }) {
        const { issueBoardId, issueId, attributeChanged, targetColumn, targetPosition } = body;

        const state = getState() as IRootState;
        const issuesOnBoard = selectHttpIssuesOnBoard(state);
        const issue = issuesOnBoard.find((i) => i._id === issueId);
        const kanbanBoardColumns = selectKanbanBoardColumns(state);
        const colIndexByValue = (value: string) => kanbanBoardColumns.findIndex((c) => c.value === value);

        let toColumnId = -1;
        if (attributeChanged === "bucket") {
          toColumnId = colIndexByValue(targetColumn);
        } else if (attributeChanged === "status") {
          toColumnId = colIndexByValue(targetColumn);
        } else {
          toColumnId = colIndexByValue(targetColumn);
        }

        let fromColumnId = -1;
        let fromPosition = 0;

        if (attributeChanged === "bucket") {
          const fromValue = issue?.bucket ?? "";
          fromColumnId = colIndexByValue(fromValue);
        } else if (attributeChanged === "status") {
          const fromValue = issue?.status ?? "";
          fromColumnId = colIndexByValue(fromValue);
        } else {
          const fromValue = classifyTidsfrist(issue?.dates?.due ?? null);
          fromColumnId = colIndexByValue(fromValue);
        }

        if (fromColumnId >= 0) {
          const col = kanbanBoardColumns[fromColumnId];
          const cards = col?.cards ?? [];
          const idxInCol = cards.findIndex((card) => sameId(card.content._id, issueId));
          fromPosition =
            idxInCol >= 0
              ? idxInCol
              : issue != null
              ? getKanbanOrderValue(issue, attributeChanged as KanbanOrderAttribute)
              : 0;
        }

        const kanbanTo: IKanbanTo = { toColumnId, toPosition: targetPosition };
        const kanbanFrom: IKanbanFrom = { fromColumnId, fromPosition };
        const updatedBoard: IKanbanBoard<IIssueApi> = moveCard({ columns: kanbanBoardColumns }, kanbanFrom, kanbanTo);

        const posById = new Map<string, { column: string; index: number }>();
        for (const col of updatedBoard.columns) {
          for (let i = 0; i < col.cards.length; i++) {
            const c = col.cards[i] as IKanbanCard<IIssueApi>;
            const id = String(c.content._id);
            posById.set(id, { column: col.value as string, index: i });
          }
        }

        const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);
        const undos = argsList.map((args) =>
          dispatch(
            openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
              const attr = attributeChanged as KanbanOrderAttribute;

              for (let i = 0; i < draft.length; i++) {
                const it = draft[i];
                const pos = posById.get(String(it._id));
                if (!pos) continue;

                const nextKo = { ...(it.kanbanOrder ?? {}), [attr]: pos.index };
                let nextItem: IIssueApi = it;
                if (attr !== "tidsfrist") {
                  nextItem = { ...nextItem, [attr]: pos.column } as IIssueApi;
                } else if (sameId(it._id, issueId)) {
                  const nextDue = computeDueForTidsfristBucket(pos.column, nextItem?.dates?.due ?? null);
                  nextItem = {
                    ...nextItem,
                    dates: {
                      ...(nextItem.dates ?? {}),
                      due: nextDue?.toISOString() ?? null,
                    },
                  } as IIssueApi;
                }

                if (getKanbanOrderValue(it, attr) !== pos.index) {
                  nextItem = { ...nextItem, kanbanOrder: nextKo } as IIssueApi;
                }

                if (nextItem !== it) draft[i] = nextItem;
              }

              draft.sort((a, b) => getKanbanOrderValue(a, attr) - getKanbanOrderValue(b, attr));
            }),
          ),
        );

        try {
          const { data } = (await queryFulfilled) as { data: PutProjectByProjectIdIssuesKanbanreorderApiResponse };

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
                const attr = body.attributeChanged as KanbanOrderAttribute;
                const updatedIssue = data.updatedIssue as IIssueApi;
                const idx = draft.findIndex((i) => sameId(i._id, updatedIssue._id));
                if (idx >= 0) {
                  draft[idx] = updatedIssue;
                }
                for (const ch of data.changed) {
                  const changedIdx = draft.findIndex((i) => String(i._id) === String(ch.id));
                  if (changedIdx >= 0) {
                    const it = draft[changedIdx];
                    draft[changedIdx] = {
                      ...it,
                      kanbanOrder: {
                        ...(it.kanbanOrder ?? {}),
                        [attributeChanged]: ch.index,
                      },
                    };
                  }
                }
                if (attr !== "tidsfrist") {
                  const movedIdx = draft.findIndex((i) => String(i._id) === String(data.moved.id));
                  if (movedIdx >= 0) {
                    const it = draft[movedIdx];
                    draft[movedIdx] = { ...it, [attr]: data.moved.column };
                  }
                }
                draft.sort((a, b) => getKanbanOrderValue(a, attr) - getKanbanOrderValue(b, attr));
              }),
            );
          }
          dispatch(openapi.util.invalidateTags(["Issue", "ConnectedIssues", "NotificationIssueStatuses"]));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke flytte sak"));
          undos.forEach((u) => u.undo?.());
        }
      },
    },
    putProjectByProjectIdIssuesUpdateGantActivity: {
      invalidatesTags: ["Issue"],
      async onQueryStarted(resp, { dispatch, queryFulfilled, getState }) {
        await patchAllIssuesAfterSingleIssueUpdate(
          {
            projectId: resp.projectId,
            issueId: resp.body.issueId,
            attr: "gantActivities",
            value: resp.body.gantActivities,
          },
          { dispatch, queryFulfilled, getState },
        );
      },
    },
    putProjectByProjectIdIssueUpdateUser: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueUpdateSimple: {
      async onQueryStarted({ projectId, body }, { dispatch, getState, queryFulfilled }) {
        const undoPatches = patchIssueOptimistically(projectId, body._id, body.property, getState, dispatch);

        try {
          const { data: issue } = await queryFulfilled;
          updateIssueAfterResp(projectId, issue, getState, dispatch);
          if (body.property.tags != null) {
            dispatch(apiSlice.util.invalidateTags(["Tags"]));
          }
          if (
            shouldInvalidateIssueFormulasAfterPropertyPatch(projectId, issue, body.property, getState() as IRootState)
          ) {
            dispatch(apiSlice.util.invalidateTags(["IssueFormulas"]));
          }
        } catch {
          undoPatches.forEach((patch) => patch.undo?.());
          dispatch(setShowFailedMessageThunk("Kunne ikke lagre endringer"));
        }
      },
    },
    putProjectByProjectIdIssuesUpdateSimpleMany: {
      async onQueryStarted({ projectId, body }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          for (const issue of data.updatedIssues as IIssueApi[]) {
            updateIssueAfterResp(projectId, issue, getState, dispatch);
          }

          if (body.property.tags != null) {
            dispatch(apiSlice.util.invalidateTags(["Tags"]));
          }

          const shouldInvalidateFormulas = (data.updatedIssues as IIssueApi[]).some((issue) =>
            shouldInvalidateIssueFormulasAfterPropertyPatch(projectId, issue, body.property, getState() as IRootState),
          );

          if (shouldInvalidateFormulas) {
            dispatch(apiSlice.util.invalidateTags(["IssueFormulas"]));
          }
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke lagre endringer"));
        }
      },
    },
    putProjectByProjectIdIssueUpdateDescription: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: issue } = await queryFulfilled;
          updateIssueAfterResp(projectId, issue, getState, dispatch);
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke lagre endringer"));
        }
      },
    },
    putProjectByProjectIdIssueUpdateFormulaCounter: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: issue } = await queryFulfilled;
          updateIssueAfterResp(projectId, issue, getState, dispatch);
        } catch {
          // Input-komponenten visar ett fältnära felmeddelande. Här sväljer vi lifecycle-felet
          // så RTK Query inte skickar det vidare till appens globala error boundary.
        }
      },
    },
    putProjectByProjectIdIssueConnectedBimSpaces: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueItemAndIssueIdCommentCommentIdReaction: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    postProjectByProjectIdIssueTodosAdd: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueTodosUpdate: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueTodosToggle: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueTodosReorder: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    deleteProjectByProjectIdIssueTodosDelete: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueConnectedTasksUpdateOrder: {
      invalidatesTags: ["ConnectedTasks", "Issues"],
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueSubscribe: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    getProjectByProjectIdIssuesOnBoard: {
      providesTags: ["Issues"],
    },
    getProjectByProjectIdIssuesOnBoardOldCompleted: {
      providesTags: ["Issues"],
    },
    getProjectByProjectIdIssuesOnBoardOldCompletedCount: {
      providesTags: ["Issues"],
    },
    postProjectByProjectIdIssuesCreate: {
      async onQueryStarted({ projectId, body }, { dispatch, queryFulfilled, getState }) {
        try {
          const resp = await queryFulfilled;
          const createdIssue = addMissingKeysIssue(resp.data, "") as IIssueApi;
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, body.issueBoardId);

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                if (!draft.some((issue) => issue._id === createdIssue._id)) {
                  draft.push(createdIssue as (typeof draft)[number]);
                }
              }),
            );
          }

          dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.ISSUE_ADDED));
          dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch (err) {
          console.warn("postProjectByProjectIdIssuesCreate.onQueryStarted handled error:", err);
        }
      },
    },
    putProjectByProjectIdIssueArchive: {
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        const { data: issue } = await queryFulfilled;
        const resp = await queryFulfilled;
        const taskOrIssue = resp.data.taskOrIssue;
        const projectId = resp.data.project;
        const issueBoardId = resp.data.issueBoard._id;
        const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);

        for (const args of argsList) {
          dispatch(
            openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
              const index = draft.findIndex((i) => i._id === issue._id);
              if (index >= 0) draft.splice(index, 1);
            }),
          );
        }

        for (const args of getOldCompletedIssuesOnBoardArgs(getState, trpcSlice)) {
          dispatch(
            openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoardOldCompleted", args, (draft) => {
              const index = draft.findIndex((i) => i._id === issue._id);
              if (index >= 0) draft.splice(index, 1);
            }),
          );
        }

        if (taskOrIssue === "TASK") {
          removeConnectedTaskFromParentIssueCaches({
            dispatch,
            getState,
            issueBoardId,
            parentIssueId: resp.data.connectedIssue,
            projectId,
            taskId: issue._id,
          });

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                draft.map((draftIssue) => {
                  if (draftIssue._id === resp.data.connectedIssue?._id) {
                    const idx = (draftIssue.connectedTasks as IIssueApi[]).findIndex((t) => t._id === issue._id);
                    if (idx >= 0) (draftIssue.connectedTasks as IIssueApi[]).splice(idx, 1);
                  }
                  return draftIssue;
                });
              }),
            );
          }

          for (const args of getOldCompletedIssuesOnBoardArgs(getState, trpcSlice)) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoardOldCompleted", args, (draft) => {
                draft.map((draftIssue) => {
                  if (draftIssue._id === resp.data.connectedIssue?._id) {
                    const idx = (draftIssue.connectedTasks as IIssueApi[]).findIndex((t) => t._id === issue._id);
                    if (idx >= 0) (draftIssue.connectedTasks as IIssueApi[]).splice(idx, 1);
                  }
                  return draftIssue;
                });
              }),
            );
          }
        }

        dispatch(removeSelectedIssueIdsInRedux({ issueBoardId, issueIds: [issue._id] }));
        dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
        dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
      },
      invalidatesTags: ["Issue"],
    },
    putProjectByProjectIdIssuesArchiveMany: {
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const archivedIssueIds = new Set(data.archivedIssueIds);
          const archivedIssues = data.archivedIssues as IIssueApi[];
          const archivedIssueIdsByBoardId = archivedIssues.reduce<Record<string, string[]>>((acc, issue) => {
            const issueBoardId = issue.issueBoard?._id;
            if (issueBoardId == null) {
              return acc;
            }

            acc[issueBoardId] = [...(acc[issueBoardId] ?? []), issue._id];
            return acc;
          }, {});

          const removeArchivedIssuesFromDraft = (draft: IIssueApi[]) => {
            for (let i = draft.length - 1; i >= 0; i--) {
              if (archivedIssueIds.has(draft[i]._id)) {
                draft.splice(i, 1);
                continue;
              }

              const connectedTasks = draft[i].connectedTasks as IIssueApi[] | undefined;
              if (Array.isArray(connectedTasks)) {
                for (let taskIndex = connectedTasks.length - 1; taskIndex >= 0; taskIndex--) {
                  if (archivedIssueIds.has(connectedTasks[taskIndex]._id)) {
                    connectedTasks.splice(taskIndex, 1);
                  }
                }
              }
            }
          };

          for (const args of getDoGetAllIssuesArgs(getState, trpcSlice)) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
                removeArchivedIssuesFromDraft(draft);
              }),
            );
          }

          for (const args of getOldCompletedIssuesOnBoardArgs(getState, trpcSlice)) {
            dispatch(
              openapi.util.updateQueryData(
                "getProjectByProjectIdIssuesOnBoardOldCompleted",
                args,
                (draft: IIssueApi[]) => {
                  removeArchivedIssuesFromDraft(draft);
                },
              ),
            );
          }

          Object.entries(archivedIssueIdsByBoardId).forEach(([issueBoardId, issueIds]) => {
            dispatch(removeSelectedIssueIdsInRedux({ issueBoardId, issueIds }));
          });

          dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke arkivere valgte saker"));
          console.warn("putProjectByProjectIdIssuesArchiveMany failed:", err);
        }
      },
      invalidatesTags: ["Issue"],
    },
    putProjectByProjectIdIssueConvertType: {
      invalidatesTags: ["Issue", "Issues", "ConnectedTasks", "IssueLogs"],
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: issue } = await queryFulfilled;
          updateIssueAfterResp(projectId, issue, getState, dispatch);
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke endre type"));
          console.warn("putProjectByProjectIdIssueConvertType failed:", err);
        }
      },
    },
    putIssueConnect: {
      invalidatesTags: ["ConnectedIssues"],
      async onQueryStarted({ body }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const { projectId, issueBoardId } = body;
          const { source, target } = data;
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);
          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
                let hit = 0;
                for (let i = 0; i < draft.length; i++) {
                  const id = draft[i]._id;
                  if (id === source._id) {
                    draft[i] = source;
                    if (++hit === 2) break;
                  } else if (id === target._id) {
                    draft[i] = target;
                    if (++hit === 2) break;
                  }
                }
              }),
            );
          }
          dispatch(setShowSuccessMessageThunk("Kobling oppdatert"));
        } catch (err) {
          dispatch(
            setShowFailedMessageThunk(
              body.action === "DISCONNECT" ? "Kunne ikke fjerne kobling" : "Kunne ikke opprette kobling",
            ),
          );
          console.warn("putIssueConnect failed:", err);
        }
      },
    },
    postProjectByProjectIdIssuesCopy: {
      async onQueryStarted(copyArgs, { dispatch, queryFulfilled, getState }) {
        try {
          const resp = await queryFulfilled;
          const createdIssues = (Array.isArray(resp.data) ? resp.data : [resp.data]) as IIssueApi[];
          const firstCreatedIssue = createdIssues[0];

          if (firstCreatedIssue == null) {
            return;
          }

          const args = getDoGetAllIssuesArgsForBoard(
            getState,
            trpcSlice,
            copyArgs.projectId,
            firstCreatedIssue.issueBoard._id,
          );

          if (createdIssues.some((issue) => issue.taskOrIssue !== "TASK")) {
            dispatch(setIssueSortedColInRedux(null));
          }

          for (const arg of args) {
            const createdIssuesForArg = createdIssues.filter((issue) => {
              if (String(issue.issueBoard?._id) !== String(arg.issueboardId)) {
                return false;
              }

              if (issue.taskOrIssue === "TASK") {
                if (getIdString(issue.connectedIssue) != null) {
                  return false;
                }

                return arg.includeTasks === "true";
              }

              return arg.includeIssues === "true";
            });

            if (createdIssuesForArg.length === 0) {
              continue;
            }

            const createdIssueIds = new Set(createdIssuesForArg.map((issue) => issue._id));

            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", arg, (draft: IIssueApi[]) => {
                draft.unshift(...createdIssuesForArg);
                for (let i = draft.length - 1; i >= createdIssuesForArg.length; i--) {
                  if (createdIssueIds.has(draft[i]._id)) {
                    draft.splice(i, 1);
                  }
                }
              }),
            );
          }

          for (const createdIssue of createdIssues) {
            if (createdIssue.taskOrIssue !== "TASK") {
              continue;
            }

            const parentIssueId = getIdString(createdIssue.connectedIssue);
            const issueBoardId = getIdString(createdIssue.issueBoard);
            if (parentIssueId == null || issueBoardId == null) {
              continue;
            }

            addConnectedTaskToParentIssueCaches({
              dispatch,
              getState,
              issueBoardId,
              parentIssueId,
              projectId: copyArgs.projectId,
              task: createdIssue,
            });
          }

          const copiedIssueIdsByBoardId = createdIssues.reduce<Record<string, string[]>>((acc, issue) => {
            if (issue.taskOrIssue === "TASK") {
              return acc;
            }

            const issueBoardId = issue.issueBoard?._id;
            if (issueBoardId == null) {
              return acc;
            }

            acc[issueBoardId] = [...(acc[issueBoardId] ?? []), issue._id];
            return acc;
          }, {});

          Object.entries(copiedIssueIdsByBoardId).forEach(([issueBoardId, issueIds]) => {
            dispatch(addSelectedIssueIdsInRedux({ issueBoardId, issueIds }));
          });

          dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke kopiere sak"));
          console.error("Issue copy failed:", err);
        }
      },
    },
    getIssueByUser: {
      providesTags: ["TrpcMembersByUserRelation"],
    },
    putProjectByProjectIdIssueUpdateDates: {
      async onQueryStarted({ projectId, body }, { dispatch, getState, queryFulfilled }) {
        const { issueId, dates } = body;
        const issuesOnBoard = selectHttpIssuesOnBoard(getState() as IRootState);
        const issue = issuesOnBoard.find((i) => sameId(i._id, issueId));

        if (!issue) return;

        const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issue.issueBoard._id);
        const undos = argsList.map((args) =>
          dispatch(
            openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
              const idx = draft.findIndex((i) => sameId(i._id, issueId));
              if (idx >= 0) {
                draft[idx] = {
                  ...draft[idx],
                  dates: {
                    ...draft[idx].dates,
                    plannedStart: dates.plannedStart,
                    due: dates.due,
                  },
                };
              }
            }),
          ),
        );

        try {
          const { data } = await queryFulfilled;

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
                const idx = draft.findIndex((i) => sameId(i._id, data.updatedIssue._id));
                if (idx >= 0) {
                  draft[idx] = data.updatedIssue;
                }
              }),
            );
          }
          dispatch(setShowSuccessMessageThunk("Datoer oppdatert"));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke oppdatere datoer"));
          undos.forEach((u) => u.undo?.());
        }
      },
    },
    getProjectByProjectIdTableTemplates: {
      providesTags: ["IssueTableTemplates"],
    },
    patchProjectByProjectIdTableTemplate: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled; // wait for backend success
          dispatch(openapi.util.invalidateTags(["IssueTableTemplates"]));
          dispatch(apiSlice.util.invalidateTags(["SingleIssueBoard"]));
          dispatch(
            setShowSuccessMessageThunk(arg?.body?.tableTemplate?.archived === true ? "Mal arkivert" : "Mal lagret"),
          );
        } catch (err) {
          dispatch(
            setShowFailedMessageThunk(
              arg?.body?.tableTemplate?.archived === true ? "Kunne ikke arkivere mal" : "Kunne ikke lagre mal",
            ),
          );
          console.error("Table template save failed:", err);
        }
      },
    },
    postProjectByProjectIdTableTemplate: {
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          dispatch(openapi.util.invalidateTags(["IssueTableTemplates"]));
          dispatch(apiSlice.util.invalidateTags(["SingleIssueBoard"]));
          dispatch(setShowSuccessMessageThunk("Mal opprettet"));
          dispatch({ type: SET_ACTIVE_TEMPLATE_ACTION, payload: resp.data._id });
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke opprette mal"));
          console.error("Table template create failed:", err);
        }
      },
    },
    getProjectByProjectIdConnectedTasks: {
      providesTags: ["ConnectedTasks"],
    },
    postIssueboardFavoriteToggle: {
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        const { projectId, issueboardId } = body;

        try {
          const { data } = await queryFulfilled;
          const { isFavorite } = data;

          dispatch(
            openapi.util.updateQueryData("getIssueboardFavorites", { projectId }, (draft) => {
              if (isFavorite) {
                if (!draft.some((f) => f.issueboard === issueboardId)) {
                  draft.push({ _id: "", user: "", issueboard: issueboardId, project: projectId });
                }
              } else {
                const idx = draft.findIndex((f) => f.issueboard === issueboardId);
                if (idx >= 0) draft.splice(idx, 1);
              }
            }),
          );
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke oppdatere favoritt"));
        }
      },
    },
    getProjectByProjectIdIssueAndIssueIdLogs: {
      providesTags: ["IssueLogs"],
    },
  },
});

export const updateIssueAfterResp = async (
  projectId: string,
  issue: IIssueApi,
  getState: AppGetState,
  dispatch: AppDispatch,
) => {
  const issueBoardId = issue?.issueBoard?._id;

  if (!issueBoardId || !issue?._id) {
    return;
  }

  const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);
  const argsListSingleIssue = { projectId, issueId: issue._id };

  dispatch(
    openapi.util.updateQueryData("getProjectByProjectIdIssuesGetSingle", argsListSingleIssue, (draft: IIssueApi) => {
      if (draft && sameId(draft._id, issue._id)) {
        Object.assign(draft, issue);
      }
    }),
  );

  for (const args of argsList) {
    dispatch(
      openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
        const idx = draft.findIndex((i) => sameId(i._id, issue._id));
        if (idx >= 0) {
          draft[idx] = issue;
        }
      }),
    );
  }

  for (const args of getOldCompletedIssuesOnBoardArgs(getState, trpcSlice)) {
    dispatch(
      openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoardOldCompleted", args, (draft: IIssueApi[]) => {
        const idx = draft.findIndex((i) => sameId(i._id, issue._id));
        if (idx >= 0) {
          draft[idx] = issue;
        }
      }),
    );
  }

  patchMultiRegisterIssueCaches(issue, getState, dispatch);

  if (issue.taskOrIssue === "TASK") {
    const connectedIssueId = issue.connectedIssue?._id;

    for (const args of argsList) {
      dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
          for (const parentIssue of draft) {
            if (!sameId(parentIssue._id, connectedIssueId)) continue;
            const taskIndex = parentIssue.connectedTasks?.findIndex((task) => sameId(task._id, issue._id)) ?? -1;
            if (taskIndex >= 0 && parentIssue.connectedTasks != null) {
              parentIssue.connectedTasks[taskIndex] = issue;
            }
            return;
          }
        }),
      );
    }

    for (const args of getOldCompletedIssuesOnBoardArgs(getState, trpcSlice)) {
      dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoardOldCompleted", args, (draft: IIssueApi[]) => {
          for (const parentIssue of draft) {
            if (!sameId(parentIssue._id, connectedIssueId)) continue;
            const taskIndex = parentIssue.connectedTasks?.findIndex((task) => sameId(task._id, issue._id)) ?? -1;
            if (taskIndex >= 0 && parentIssue.connectedTasks != null) {
              parentIssue.connectedTasks[taskIndex] = issue;
            }
            return;
          }
        }),
      );
    }

    for (const args of getConnectedTasksArgs(getState, trpcSlice)) {
      dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdConnectedTasks", args, (draft: IIssueApi[]) => {
          const taskIndex = draft.findIndex((task) => sameId(task._id, issue._id));
          if (taskIndex >= 0) {
            draft[taskIndex] = issue;
          }
        }),
      );
    }
  }

  const notifications = (getState() as IRootState).globalReducer.user?.notifications;
  if (Array.isArray(notifications) && notifications.some((notification) => sameId(notification.itemDBid, issue._id))) {
    dispatch(
      setNotificationInRedux(
        notifications.map((notification) =>
          sameId(notification.itemDBid, issue._id) ? { ...notification, issueStatus: issue.status } : notification,
        ),
      ),
    );
  }

  dispatch(openapi.util.invalidateTags([{ type: "IssueLogs" }]));
  dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
  dispatch(openapi.util.invalidateTags([{ type: "IssueLogs" }, { type: "FormulaGroupIssues" }]));
};

export const patchAllIssuesAfterSingleIssueUpdate = async (
  mutationArgs: {
    projectId: string;
    issueId: string;
    attr?: string;
    value?: PatchableIssueValue;
  },
  {
    dispatch,
    queryFulfilled,
    getState,
  }: { dispatch: AppDispatch; queryFulfilled: Promise<{ data: IUpdateIssueResp }>; getState: AppGetState },
) => {
  const { issueId, projectId } = mutationArgs;

  let resp: { data: IUpdateIssueResp };

  try {
    resp = await queryFulfilled;
  } catch (err) {
    dispatch(setShowFailedMessageThunk("Kunne ikke lagre endringer"));
    console.warn("patchAllIssuesAfterSingleIssueUpdate: query rejected", err);
    return;
  }

  dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation"]));
  dispatch(openapi.util.invalidateTags(["ConnectedIssues"]));
  dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));

  const data = resp.data;
  const updatedIssue = data?.updatedIssue;

  if (!updatedIssue) {
    console.warn("No updatedIssue in response");
    return;
  }

  const issueBoardId = updatedIssue.issueBoard._id;

  const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);
  patchMultiRegisterIssueCaches(updatedIssue, getState, dispatch);
  patchMeetingConnectedIssueCaches(updatedIssue, getState, dispatch);

  for (const args of argsList) {
    dispatch(
      openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
        for (let i = 0; i < draft.length; i++) {
          if (draft[i]._id === issueId) {
            draft[i] = updatedIssue;
            break;
          }
          try {
            if (draft[i].connectedTasksAndIssuesFromOtherBoards?.length > 0) {
              for (let j = 0; j < draft[i].connectedTasksAndIssuesFromOtherBoards!.length; j++) {
                if (draft[i].connectedTasksAndIssuesFromOtherBoards[j]._id === issueId) {
                  draft[i].connectedTasksAndIssuesFromOtherBoards[j] = {
                    ...updatedIssue,
                    issueBoard: updatedIssue.issueBoard._id,
                  };
                  break;
                }
              }
            }
          } catch (err) {
            console.warn("Error updating issue in cache:", err);
          }
        }
      }),
    );
  }

  if (updatedIssue.taskOrIssue === "TASK") {
    const connectedIssueId = updatedIssue.connectedIssue?._id;
    if (connectedIssueId) {
      for (const args of argsList) {
        dispatch(
          openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
            for (const issue of draft) {
              if (issue._id !== connectedIssueId) continue;
              if (!issue.connectedTasks) break;

              for (let t = 0; t < issue.connectedTasks.length; t++) {
                if (issue.connectedTasks[t]._id === issueId) {
                  issue.connectedTasks[t] = updatedIssue;
                  return;
                }
              }
              return;
            }
          }),
        );
      }
    }
  }

  if (Array.isArray(resp.data.issuesWithKanbanOrder) && resp.data.issuesWithKanbanOrder.length > 0) {
    const byId = new Map(resp.data.issuesWithKanbanOrder.map((x) => [x._id, x.kanbanOrder]));
    for (const args of argsList) {
      const patchResult = dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
          for (const issue of draft) {
            const ko = byId.get(issue._id);
            if (ko !== undefined) issue.kanbanOrder = ko;
          }
        }),
      );
      if (patchResult.patches.length === 0) {
        console.warn("No kanban patches were made to the cache");
      }
    }
  }
};

const patchMeetingConnectedIssueCaches = (updatedIssue: IIssueApi, getState: AppGetState, dispatch: AppDispatch) => {
  const argsList = getMeetingArgs(getState, trpcSlice);

  for (const args of argsList) {
    dispatch(
      openapi.util.updateQueryData("getProjectByProjectIdMeetingAndMeetingId", args, (draft) => {
        for (const agendaItem of draft?.agendaItems ?? []) {
          if (agendaItem == null || typeof agendaItem !== "object" || !("connectedIssues" in agendaItem)) {
            continue;
          }

          const connectedIssues = Array.isArray(agendaItem.connectedIssues) ? agendaItem.connectedIssues : [];

          for (let issueIndex = 0; issueIndex < connectedIssues.length; issueIndex++) {
            const connectedIssue = connectedIssues[issueIndex];
            if (connectedIssue == null || typeof connectedIssue !== "object") {
              continue;
            }

            if (sameId(connectedIssue?._id, updatedIssue._id)) {
              Object.assign(connectedIssue, updatedIssue);
              continue;
            }

            if (!("connectedTasks" in connectedIssue)) {
              continue;
            }

            const connectedTasks = Array.isArray(connectedIssue.connectedTasks) ? connectedIssue.connectedTasks : [];
            const taskIndex = connectedTasks.findIndex(
              (task) => task != null && typeof task === "object" && sameId(task._id, updatedIssue._id),
            );

            const connectedTask = connectedTasks[taskIndex];
            if (connectedTask != null && typeof connectedTask === "object") {
              Object.assign(connectedTask, updatedIssue);
            }
          }
        }
      }),
    );
  }
};
